// Linking this browser (docs/07 "Enrolment and activation"): a user's first
// device needs their one-time device code (from the invitation or an administrator; the
// owner may use the host's bootstrap code); once one of their devices
// is active, a new browser enrols without one and waits for that device's approval.
import { Link as LinkIcon } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { messages, useT } from "../i18n";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { ErrorState } from "../ui/states";

export interface LinkBrowserFormProps {
  /** Whether the signed-in user has an active device yet (from `GET /api/devices`). */
  hasActiveDevice: boolean;
  error?: string;
  link(name: string, bootstrapCode?: string): Promise<void>;
  className?: string;
}

export function LinkBrowserForm({ hasActiveDevice, error, link, className }: LinkBrowserFormProps) {
  const t = useT().devices.link;
  const [name, setName] = useState(defaultName());
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await link(name.trim() || defaultName(), hasActiveDevice ? undefined : code.trim());
    } catch {
      // The provider puts the error into the device state, shown below.
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={cn(cardClass, "p-5 lg:p-6", className)} aria-label={t.form} onSubmit={(e) => void onSubmit(e)}>
      <h3 className="text-base font-semibold text-gray-900">{t.title}</h3>
      <p className="mb-0 mt-0.5 text-[13px] text-gray-500">
        {t.lead} {hasActiveDevice ? t.leadLater : t.leadFirst}
      </p>
      <ol className="my-5 grid list-none gap-3 p-0 sm:grid-cols-3">
        {(hasActiveDevice ? t.laterSteps : t.firstSteps).map((s, i) => (
          <li key={s} className="flex items-center gap-2.5 rounded-lg bg-gray-50 p-3 text-[13px] text-gray-700 dark:bg-gray-100/50">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {i + 1}
            </span>
            {s}
          </li>
        ))}
      </ol>
      {error ? <ErrorState className="mb-4" title={t.notLinked} message={error} /> : null}
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        {hasActiveDevice ? null : (
          <Input
            className="sm:col-span-2"
            label={t.code}
            placeholder="XXXXX-XXXXX"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={16}
            required
            size="lg"
            inputClassName="font-mono tracking-[0.2em] uppercase"
          />
        )}
        <Input label={t.name} value={name} onChange={(e) => setName(e.target.value)} maxLength={64} />
        <Button type="submit" className="gap-1.5" isLoading={busy}>
          <LinkIcon weight="bold" className="h-4 w-4" /> {t.submit}
        </Button>
      </div>
      <p className="mb-0 mt-4 text-xs text-gray-500">
        {t.keysNote}
        {hasActiveDevice ? "" : t.codeNote}
      </p>
    </form>
  );
}

function defaultName(): string {
  const ua = navigator.userAgent;
  if (/Firefox/.test(ua)) return "Firefox";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome/.test(ua)) return "Chrome";
  if (/Safari/.test(ua)) return "Safari";
  return messages().devices.link.defaultName;
}
